// src/main.js
import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import store from './store';
import apiClient from './apiClient';
import { i18n, setI18nLocale } from './i18n';

// Import Vue Toastification
import Toast, { POSITION } from 'vue-toastification';
import 'vue-toastification/dist/index.css';
import './assets/toast-overrides.css';
import './assets/app-pill-btn.css';

// Publish the live navbar height as `--site-nav-height` /
// `--fs2-nav-h` on the root element BEFORE the app mounts so any
// view (Gallery2, FaceSwap2, Studio2 modals) can pad away from
// the fixed app navbar without each component re-deriving it.
import { attachNavHeightWatcher } from './utils/navHeight';

// Create the Vue app instance
const app = createApp(App);

// Suppress Vue DOM patching errors that occur during rapid updates
app.config.errorHandler = (err, vm, info) => {
  // Suppress insertBefore/vnode errors from DOM patching during modal updates
  if (err && err.message && (
    err.message.includes('insertBefore') ||
    err.message.includes('__vnode') ||
    err.message.includes('addListener')
  )) {
    console.warn('[Vue] Suppressed extension or DOM patching error:', err.message);
    return;
  }
  // Log other errors normally
  console.error('[Vue Error]', err, info);
};

// Global error listener for non-Vue errors (like the extension one in workers/blobs)
window.addEventListener('error', (event) => {
  if (event.message && event.message.includes('addListener')) {
    event.stopImmediatePropagation();
    console.warn('[System] Suppressed browser extension error:', event.message);
  }
  // Benign ResizeObserver warning that spams the webpack dev overlay
  if (event.message && event.message.includes('ResizeObserver loop')) {
    event.stopImmediatePropagation();
    event.preventDefault();
  }
}, true);

// Make the apiClient available globally if needed
app.config.globalProperties.$apiClient = apiClient;

// Use router, store, and i18n
app.use(router);
app.use(store);
app.use(i18n);

// Register Vue Toastification. All visual styling lives in
// assets/toast-overrides.css now (floating glass card + per-status
// outline tint), so we don't pass per-type inline `style`s here —
// the CSS file is the single source of truth.
app.use(Toast, {
  position: POSITION.TOP_RIGHT,
  timeout: 10000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  toastClassName: 'custom-toast',
  bodyClassName: 'custom-toast-body',
  icon: true,
});

// Ensure the session + initial locale are loaded before mounting the app
Promise.all([
  store.dispatch('loadSession'),
  setI18nLocale().catch(() => {}),
]).then(() => {
  app.mount('#app');
  // Wait one frame so the navbar element exists before measuring.
  requestAnimationFrame(() => attachNavHeightWatcher());
});
